import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { fetchOrders, isToday, isTomorrow, getBinSize, fmtTime } from '../services/orderService';
import { fetchFlaggedTasks } from '../services/taskService';
import { queryKeys } from '../lib/queryClient';
import {
  Text,
  Badge,
  Button,
  Avatar,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  VehicleTruck24Regular,
  ArrowDown24Regular,
  ArrowSwap24Regular,
  Warning24Regular,
  Person24Regular,
  ChevronDown24Regular,
  ChevronUp24Regular,
  ShoppingBag24Regular,
  Box24Regular,
  Wrench24Regular,
  ArrowRight16Regular,
  PersonAdd24Regular,
  Flag24Regular,
} from '@fluentui/react-icons';

// ── Types ──────────────────────────────────────────────────────────────────
type JobStatus = 'completed' | 'en_route' | 'scheduled' | 'problem' | 'unassigned';
type JobType   = 'Delivery' | 'Pickup' | 'Swap';

interface Job {
  id: string;
  time: string;
  type: JobType;
  customer: string;
  address: string;
  bin: string;
  driver: string | null;
  status: JobStatus;
}

// ── Styles ─────────────────────────────────────────────────────────────────
const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflowX: 'hidden',
    marginLeft: '-1px',
  },

  // Hero
  hero: {
    background: 'linear-gradient(135deg, #F7834F 0%, #F7B74F 60%, #FDDCB0 100%)',
    ...shorthands.padding('56px', '40px'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    position: 'relative',
    flexShrink: '0',
    ...shorthands.overflow('hidden'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('32px', '16px'),
      gap: '24px',
    },
  },
  heroGreeting: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#ffffff',
    textShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  heroQuickRow: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gap: '12px',
    zIndex: '1',
    // Four cards in one row overflow the hero on phones (it clips via
    // overflow:hidden). Wrap into a 2x2 grid that fills the hero width.
    '@media (max-width: 768px)': {
      gridAutoFlow: 'row',
      gridTemplateColumns: '1fr 1fr',
      width: '100%',
    },
  },
  heroQuickCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ...shorthands.padding('14px', '20px'),
    ...shorthands.borderRadius('12px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    cursor: 'pointer',
    ...shorthands.transition('all', '0.15s', 'ease'),
    '&:hover': { boxShadow: tokens.shadow16 },
    '&:hover > :first-child': { transform: 'scale(1.15)' },
  },
  heroQuickIconCircle: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('50%'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease',
  },
  heroQuickLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },

  // Dashboard body
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('24px', '28px'),
    '@media (max-width: 768px)': {
      ...shorthands.padding('16px', '12px'),
    },
  },

  // Alerts
  alertStrip: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    '&:hover': { filter: 'brightness(0.97)' },
  },
  alertBannerText: {
    flex: '1',
    fontSize: '13px',
    fontWeight: '600',
  },
  alertBannerSub: {
    fontSize: '12px',
    fontWeight: '400',
    opacity: '0.85',
    marginLeft: '4px',
  },

  // Stat row (bins + job count)
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    // 4-up KPIs on a 375px viewport are unreadable. Drop to 2x2 below
    // 768px, then a single column below 480px so each card's value +
    // label has breathing room.
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('18px', '20px'),
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    cursor: 'pointer',
    ...shorthands.transition('box-shadow', '0.15s', 'ease'),
    '&:hover': { boxShadow: tokens.shadow8 },
  },
  statIconWrap: {
    width: '44px',
    height: '44px',
    ...shorthands.borderRadius('10px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  statBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statValue: {
    fontSize: '26px',
    fontWeight: '700',
    lineHeight: '1',
    color: tokens.colorNeutralForeground1,
  },
  statLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontWeight: '500',
  },

  // Main row: job board + drivers
  mainRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 280px',
    gap: '16px',
    alignItems: 'start',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },

  // Panel (shared by job board + driver panel)
  panel: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    flexDirection: 'column',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('14px', '20px', '12px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  panelTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },

  // Driver list (inside right panel)
  driverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': { borderBottom: 'none' },
  },
  driverInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
  driverName: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  driverStatus: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  driverBadge: {
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: '0',
  },

  // Job board table
  columnHeaders: {
    display: 'grid',
    gridTemplateColumns: '60px 100px 1fr 52px 120px',
    gap: '8px',
    ...shorthands.padding('8px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    // The 5-col fixed grid (~364px) overflows a phone-width panel. Drop the
    // Type (2nd) and Bin (4th) cells and keep Time | Customer | Status.
    '@media (max-width: 768px)': {
      gridTemplateColumns: '52px 1fr auto',
      ...shorthands.padding('8px', '12px'),
      '& > *:nth-child(2)': { display: 'none' },
      '& > *:nth-child(4)': { display: 'none' },
    },
  },
  colHeader: {
    fontSize: '11px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  jobRow: {
    display: 'grid',
    gridTemplateColumns: '60px 100px 1fr 52px 120px',
    gap: '8px',
    alignItems: 'center',
    ...shorthands.padding('11px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
    // Match columnHeaders: hide Type (2nd) + Bin (4th) cells on phones.
    '@media (max-width: 768px)': {
      gridTemplateColumns: '52px 1fr auto',
      ...shorthands.padding('11px', '12px'),
      '& > *:nth-child(2)': { display: 'none' },
      '& > *:nth-child(4)': { display: 'none' },
    },
  },
  jobTime: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  jobTypeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  jobTypeIcon: {
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
  },
  jobTypeLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
  },
  jobCustomer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: '0',
  },
  jobCustomerName: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  jobAddress: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  jobBin: {
    fontSize: '12px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    ...shorthands.padding('3px', '10px'),
    ...shorthands.borderRadius('12px'),
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  statusDot: {
    width: '7px',
    height: '7px',
    ...shorthands.borderRadius('50%'),
    flexShrink: '0',
  },

  // Tomorrow collapsible
  tomorrowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('14px', '20px'),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
});

// ── Status config ──────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<JobStatus, { label: string; bg: string; color: string; dotColor: string }> = {
  completed:  { label: 'Completed',  bg: '#E6F9F0', color: '#1A7F45', dotColor: '#1A7F45' },
  en_route:   { label: 'En Route',   bg: '#FFF4DE', color: '#C27820', dotColor: '#E8A020' },
  scheduled:  { label: 'Scheduled',  bg: '#EAF2FF', color: '#1565C0', dotColor: '#4F8EF7' },
  problem:    { label: 'Problem',    bg: '#FDEAEA', color: '#C4314B', dotColor: '#E05252' },
  unassigned: { label: 'Unassigned', bg: '#F0F0F0', color: '#666666', dotColor: '#999999' },
};

const TYPE_CONFIG: Record<JobType, { icon: React.ReactNode; bg: string; color: string }> = {
  Delivery: { icon: <VehicleTruck24Regular style={{ fontSize: '14px' }} />, bg: '#EAF2FF', color: '#4F8EF7' },
  Pickup:   { icon: <ArrowDown24Regular   style={{ fontSize: '14px' }} />, bg: '#E6F9F0', color: '#3DBE7A' },
  Swap:     { icon: <ArrowSwap24Regular   style={{ fontSize: '14px' }} />, bg: '#F3EEFF', color: '#9B6CF7' },
};

function getGreeting(name: string): string {
  const hour = new Date().getHours();
  const salutation = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return `${salutation}, ${name.split(' ')[0]}`;
}

// TODO: replace with inventory table when available
const MOCK_BINS = [
  { label: 'Available',      value: 8,  color: '#3DBE7A', bg: '#E6F9F0', icon: <Box24Regular          style={{ color: '#3DBE7A' }} /> },
  { label: 'Out in Field',   value: 14, color: '#4F8EF7', bg: '#EAF2FF', icon: <VehicleTruck24Regular style={{ color: '#4F8EF7' }} /> },
  { label: 'In Maintenance', value: 2,  color: '#C27820', bg: '#FFF4DE', icon: <Wrench24Regular       style={{ color: '#C27820' }} /> },
];

// TODO: replace with driver assignments table when available
const MOCK_DRIVERS = [
  { id: '1', name: 'Rico Vasquez',  status: 'active', jobsDone: 2, jobsLeft: 3, current: 'Delivering — Heritage Homes' },
  { id: '2', name: 'Jake Morrison', status: 'active', jobsDone: 1, jobsLeft: 4, current: 'En route — Blue Ridge' },
  { id: '3', name: 'Priya Nair',    status: 'active', jobsDone: 3, jobsLeft: 1, current: 'Pickup — Gareth Williams' },
  { id: '4', name: 'Tom Eklund',    status: 'off',    jobsDone: 0, jobsLeft: 0, current: 'Off today' },
];

// ── Component ──────────────────────────────────────────────────────────────
function orderToJob(o: any, forDate: 'today' | 'tomorrow'): Job {
  const isDelivery = forDate === 'today' ? isToday(o.startDate) : isTomorrow(o.startDate);
  const type: JobType = isDelivery ? 'Delivery' : 'Pickup';
  const dateStr = isDelivery ? o.startDate : o.endDate;
  const statusMap: Record<string, JobStatus> = {
    started: 'en_route', stopped: 'completed', archived: 'completed',
  };
  const status: JobStatus = statusMap[o.booqableStatus] ?? 'scheduled';
  return {
    id: o.id,
    time: fmtTime(dateStr),
    type,
    customer: o.customerName,
    address: o.location || 'N/A',
    bin: getBinSize(o),
    driver: null, // TODO: wire to driver assignments
    status,
  };
}

export default function DispatcherDashboard() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const [tomorrowOpen, setTomorrowOpen] = useState(false);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: queryKeys.orders.all,
    queryFn: fetchOrders,
    staleTime: 2 * 60 * 1000,
  });

  const { data: flaggedTasks = [] } = useQuery({
    queryKey: ['tasks', 'flagged'],
    queryFn: fetchFlaggedTasks,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });

  const flaggedOrderIds = useMemo(
    () => new Set(flaggedTasks.map(t => t.order_id)),
    [flaggedTasks]
  );

  const todaysJobs = useMemo<Job[]>(() =>
    orders
      .filter(o => isToday(o.startDate) || isToday(o.endDate))
      .map(o => {
        const job = orderToJob(o, 'today');
        if (flaggedOrderIds.has(o.id)) job.status = 'problem';
        return job;
      })
      .sort((a, b) => a.time.localeCompare(b.time)),
    [orders, flaggedOrderIds]
  );

  const tomorrowsJobs = useMemo<Job[]>(() =>
    orders
      .filter(o => isTomorrow(o.startDate) || isTomorrow(o.endDate))
      .map(o => orderToJob(o, 'tomorrow'))
      .sort((a, b) => a.time.localeCompare(b.time)),
    [orders]
  );

  const alerts = useMemo(() => {
    const result: Array<{ severity: string; text: string; sub: string; path: string }> = [];
    if (flaggedTasks.length > 0) {
      const names = flaggedTasks.slice(0, 2).map(t => t.customer_name).join(', ');
      const more = flaggedTasks.length > 2 ? ` +${flaggedTasks.length - 2} more` : '';
      result.push({ severity: 'danger', text: `${flaggedTasks.length} flagged job${flaggedTasks.length > 1 ? 's' : ''}`, sub: `${names}${more}`, path: '/dispatch' });
    }
    const unassigned = todaysJobs.filter(j => j.status === 'unassigned' || !j.driver).length;
    if (unassigned > 0) {
      result.push({ severity: 'warning', text: `${unassigned} unassigned job${unassigned > 1 ? 's' : ''} today`, sub: 'No driver assigned yet', path: '/dispatch' });
    }
    // "Overdue return" = unpaid balance past the rental end date. The mirror
    // has no dedicated 'overdue' payment_status (#3), so this is derived purely
    // from balance + end date.
    const overdue = orders.filter(o => o.price > o.paidAmount && new Date(o.endDate) < new Date());
    if (overdue.length > 0) {
      result.push({ severity: 'danger', text: `${overdue.length} overdue return${overdue.length > 1 ? 's' : ''}`, sub: 'Bins past rental end date', path: '/orders' });
    }
    return result;
  }, [flaggedTasks, todaysJobs, orders]);

  const quickActions = [
    { icon: <VehicleTruck24Regular style={{ color: '#fff' }} />, label: 'Dispatch Jobs', color: '#F7834F', onClick: () => navigate('/dispatch') },
    { icon: <ShoppingBag24Regular  style={{ color: '#fff' }} />, label: 'New Booking',   color: '#4F8EF7', onClick: () => navigate('/booking') },
    { icon: <PersonAdd24Regular    style={{ color: '#fff' }} />, label: 'Assign Driver', color: '#3DBE7A', onClick: () => navigate('/dispatch') },
    { icon: <Flag24Regular         style={{ color: '#fff' }} />, label: 'Flag Job',      color: '#9B6CF7', onClick: () => navigate('/dispatch') },
  ];

  const completedToday = todaysJobs.filter(j => j.status === 'completed').length;

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.hero}>
        <Text className={styles.heroGreeting}>{user ? getGreeting(user.name) : 'Welcome'}</Text>
        <div className={styles.heroQuickRow}>
          {quickActions.map((item, i) => (
            <div key={i} className={styles.heroQuickCard} onClick={item.onClick}>
              <div className={styles.heroQuickIconCircle} style={{ backgroundColor: item.color }}>
                {item.icon}
              </div>
              <Text className={styles.heroQuickLabel}>{item.label}</Text>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.dashboard}>

        {/* Alerts — full width, conditional */}
        {alerts.length > 0 && (
          <div className={styles.alertStrip}>
            {alerts.map((a, i) => (
              <div
                key={i}
                className={styles.alertBanner}
                style={{
                  backgroundColor: a.severity === 'danger' ? '#FDEAEA' : '#FFF4DE',
                  color: a.severity === 'danger' ? '#C4314B' : '#C27820',
                }}
                onClick={() => navigate(a.path)}
              >
                <Warning24Regular style={{ flexShrink: 0 }} />
                <Text className={styles.alertBannerText} style={{ color: a.severity === 'danger' ? '#C4314B' : '#C27820' }}>
                  {a.text}
                  <span className={styles.alertBannerSub}>— {a.sub}</span>
                </Text>
                <ArrowRight16Regular style={{ flexShrink: 0 }} />
              </div>
            ))}
          </div>
        )}

        {/* Stat row: bins + job progress */}
        <div className={styles.statsRow}>
          {MOCK_BINS.map((b, i) => (
            <div key={i} className={styles.statCard} onClick={() => navigate('/product_groups')}>
              <div className={styles.statIconWrap} style={{ backgroundColor: b.bg }}>
                {b.icon}
              </div>
              <div className={styles.statBody}>
                <Text className={styles.statValue}>{b.value}</Text>
                <Text className={styles.statLabel}>{b.label}</Text>
              </div>
            </div>
          ))}
          <div className={styles.statCard} onClick={() => navigate('/dispatch')}>
            <div className={styles.statIconWrap} style={{ backgroundColor: '#F3EEFF' }}>
              <ShoppingBag24Regular style={{ color: '#9B6CF7' }} />
            </div>
            <div className={styles.statBody}>
              <Text className={styles.statValue}>{completedToday}/{todaysJobs.length}</Text>
              <Text className={styles.statLabel}>Jobs done today</Text>
            </div>
          </div>
        </div>

        {/* Main row: job board + driver panel */}
        <div className={styles.mainRow}>

          {/* Today's job board */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <Text className={styles.panelTitle}>Today's jobs</Text>
              <Button appearance="transparent" size="small" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => navigate('/dispatch')}>
                Full dispatch view
              </Button>
            </div>
            <div className={styles.columnHeaders}>
              <Text className={styles.colHeader}>Time</Text>
              <Text className={styles.colHeader}>Type</Text>
              <Text className={styles.colHeader}>Customer</Text>
              <Text className={styles.colHeader}>Bin</Text>
              <Text className={styles.colHeader}>Status</Text>
            </div>
            {isLoading
              ? <div style={{ padding: '20px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>Loading...</div>
              : todaysJobs.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>No jobs scheduled for today</div>
              : todaysJobs.map(job => {
                  const sc = STATUS_CONFIG[job.status];
                  const tc = TYPE_CONFIG[job.type];
                  return (
                    <div
                      key={job.id}
                      className={styles.jobRow}
                      style={{ opacity: job.status === 'completed' ? 0.55 : 1 }}
                      onClick={() => navigate('/dispatch')}
                    >
                      <Text className={styles.jobTime}>{job.time}</Text>
                      <div className={styles.jobTypeCell}>
                        <div className={styles.jobTypeIcon} style={{ backgroundColor: tc.bg, color: tc.color }}>
                          {tc.icon}
                        </div>
                        <Text className={styles.jobTypeLabel}>{job.type}</Text>
                      </div>
                      <div className={styles.jobCustomer}>
                        <Text className={styles.jobCustomerName}>{job.customer}</Text>
                        <Text className={styles.jobAddress}>{job.address}</Text>
                      </div>
                      <Text className={styles.jobBin}>{job.bin}</Text>
                      <div className={styles.statusChip} style={{ backgroundColor: sc.bg, color: sc.color }}>
                        <div className={styles.statusDot} style={{ backgroundColor: sc.dotColor }} />
                        {sc.label}
                      </div>
                    </div>
                  );
                })
            }
          </div>

          {/* Driver panel */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <Text className={styles.panelTitle}>Drivers</Text>
            </div>
            {MOCK_DRIVERS.map(d => (
              <div key={d.id} className={styles.driverItem}>
                <Avatar name={d.name} size={32} color="colorful" />
                <div className={styles.driverInfo}>
                  <Text className={styles.driverName}>{d.name}</Text>
                  <Text className={styles.driverStatus}>{d.status === 'off' ? 'Off today' : d.current}</Text>
                </div>
                {d.status === 'active' && (
                  <Text className={styles.driverBadge} style={{ color: d.jobsLeft > 0 ? '#4F8EF7' : '#1A7F45' }}>
                    {d.jobsLeft > 0 ? `${d.jobsLeft} left` : 'Done'}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tomorrow's jobs — collapsible, full width */}
        <div className={styles.panel}>
          <div className={styles.tomorrowHeader} onClick={() => setTomorrowOpen(v => !v)}>
            <Text className={styles.panelTitle}>
              Tomorrow's jobs
              <span style={{ fontSize: '12px', fontWeight: '400', color: tokens.colorNeutralForeground3, marginLeft: '10px' }}>
                {tomorrowsJobs.length} scheduled
                {tomorrowsJobs.filter(j => !j.driver).length > 0 && (
                  <span style={{ color: '#E05252', marginLeft: '6px' }}>
                    · {tomorrowsJobs.filter(j => !j.driver).length} unassigned
                  </span>
                )}
              </span>
            </Text>
            {tomorrowOpen ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
          </div>
          {tomorrowOpen && (
            <>
              <div className={styles.columnHeaders}>
                <Text className={styles.colHeader}>Time</Text>
                <Text className={styles.colHeader}>Type</Text>
                <Text className={styles.colHeader}>Customer</Text>
                <Text className={styles.colHeader}>Bin</Text>
                <Text className={styles.colHeader}>Status</Text>
              </div>
              {tomorrowsJobs.length === 0
                ? <div style={{ padding: '20px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>No jobs scheduled for tomorrow</div>
                : tomorrowsJobs.map(job => {
                    const sc = STATUS_CONFIG[job.status];
                    const tc = TYPE_CONFIG[job.type];
                    return (
                      <div key={job.id} className={styles.jobRow} onClick={() => navigate('/dispatch')}>
                        <Text className={styles.jobTime}>{job.time}</Text>
                        <div className={styles.jobTypeCell}>
                          <div className={styles.jobTypeIcon} style={{ backgroundColor: tc.bg, color: tc.color }}>
                            {tc.icon}
                          </div>
                          <Text className={styles.jobTypeLabel}>{job.type}</Text>
                        </div>
                        <div className={styles.jobCustomer}>
                          <Text className={styles.jobCustomerName}>{job.customer}</Text>
                          <Text className={styles.jobAddress}>{job.address}</Text>
                        </div>
                        <Text className={styles.jobBin}>{job.bin}</Text>
                        <div className={styles.statusChip} style={{ backgroundColor: sc.bg, color: sc.color }}>
                          <div className={styles.statusDot} style={{ backgroundColor: sc.dotColor }} />
                          {sc.label}
                        </div>
                      </div>
                    );
                  })
              }
            </>
          )}
        </div>

      </div>
    </div>
  );
}
